// src/utils/staffAssignmentLogic.js
// 🎓 教育重視版自動人員配置システム（完全改良版）

import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import dayjs from "dayjs";

// 🆕 教育システム関連のインポート
import {
  analyzeComprehensiveEducationNeeds,
  classifyEducationalRoles,
  assignEducationalStaff
} from "./educationAnalyzer";
import {
  calculateEducationValue,
  isTimeOverlap,
  EDUCATION_TYPES,
  EDUCATION_PRIORITIES,
  SKILL_THRESHOLDS
} from "./educationUtils";
import { saveEducationHistory } from "./staffEducationManager";

// 🚀 **メイン関数**: 教育重視版自動人員配置
export const runEnhancedStaffAssignment = async (date, debug = false) => {
  try {
    console.log(`🎓 ${date}の教育重視版自動人員配置を開始します...`);
    
    // 結果オブジェクト初期化（教育統計を追加）
    const result = {
      assignedSurgeries: {},
      roomAssignments: {},
      outpatientAssignments: {},
      dailyStats: {
        totalStaff: 0,
        availableStaff: 0,
        requiredStaff: 0,
        outpatientStaff: 0,
        maxConcurrentSurgeries: 0,
        trainingCapacity: 0,
        assignedStaff: 0,
        unassignedStaff: [],
        trainees: [],
        trainers: [],
        // 🆕 教育統計
        educationStats: {
          totalEducationOpportunities: 0,
          observationAssignments: 0,
          guidedPracticeAssignments: 0,
          supervisionAssignments: 0,
          assessmentAssignments: 0,
          additionalEducationAssignments: 0,
          unusedStaffCount: 0,
          utilizationRate: 0
        }
      },
      issues: [],
      warnings: [],
      recommendations: [],
      metadata: {
        enhancedMode: true,
        educationFocused: true,
        processedAt: new Date().toISOString(),
        phases: {}
      }
    };

    // Phase 1: 日勤者リストの取得（イベント参加者は時間帯別に管理）
    console.log("📋 Phase 1: 日勤者リスト取得中...");
    const { dailyStaff, eventParticipants } = await getDailyStaffWithEvents(date);
    result.dailyStats.totalStaff = dailyStaff.length;
    console.log(`✅ Phase 1完了: 日勤者=${dailyStaff.length}人, イベント参加者=${eventParticipants.length}人`);

    // Phase 2: 術前外来必要人数と同時最大手術数の計算
    console.log("🏥 Phase 2: 必要人数計算中...");
    const requirements = await calculateStaffRequirements(date);
    result.dailyStats.outpatientStaff = requirements.outpatientStaff;
    result.dailyStats.maxConcurrentSurgeries = requirements.maxConcurrentSurgeries;
    result.dailyStats.requiredStaff = requirements.totalRequired;
    result.dailyStats.trainingCapacity = Math.max(0, dailyStaff.length - requirements.totalRequired);
    console.log(`✅ Phase 2完了: 外来=${requirements.outpatientStaff}人, 同時最大手術=${requirements.maxConcurrentSurgeries}件, 最低必要=${requirements.totalRequired}人`);

    // Phase 3: 手術の重症度・難易度順ソート
    console.log("📊 Phase 3: 手術優先度ソート中...");
    const sortedSurgeries = await sortSurgeriesByPriorityAndDifficulty(requirements.surgeries);
    console.log(`✅ Phase 3完了: ${sortedSurgeries.length}件の手術をソート完了`);

    // Phase 4: スタッフ分類
    console.log("👥 Phase 4: スタッフ分類中...");
    const { experienced, trainees } = classifyStaffBySkill(dailyStaff);
    console.log(`✅ Phase 4完了: 経験者=${experienced.length}人, 育成対象=${trainees.length}人`);

    // Phase 5: 基本配置（各手術に最低限の人員を配置）
    console.log("⚙️ Phase 5: 基本配置実行中...");
    const basicAssignment = await assignBasicStaffToSurgeries(
      sortedSurgeries, 
      experienced, 
      trainees, 
      eventParticipants,
      date
    );
    result.assignedSurgeries = basicAssignment.assignments;
    result.roomAssignments = basicAssignment.roomAssignments;
    result.issues = [...result.issues, ...basicAssignment.issues];
    result.warnings = [...result.warnings, ...basicAssignment.warnings];
    console.log(`✅ Phase 5完了: 基本配置完了, 問題=${basicAssignment.issues.length}件`);

    // 🎓 Phase 6: 包括的教育配置システム（完全新規）
    console.log("🎓 Phase 6: 包括的教育配置実行中...");
    const educationResult = await assignComprehensiveEducationalSupport(
      result.assignedSurgeries,
      result.roomAssignments,
      dailyStaff,
      basicAssignment.usedStaff,
      eventParticipants,
      date
    );

    // 教育配置を統合
    Object.keys(educationResult.educationalAssignments).forEach(surgeryId => {
      if (result.assignedSurgeries[surgeryId]) {
        result.assignedSurgeries[surgeryId] = {
          ...result.assignedSurgeries[surgeryId],
          ...educationResult.educationalAssignments[surgeryId]
        };
      }
    });

    // 追加教育配置も統合
    Object.keys(educationResult.additionalEducation).forEach(surgeryId => {
      if (result.assignedSurgeries[surgeryId]) {
        result.assignedSurgeries[surgeryId] = {
          ...result.assignedSurgeries[surgeryId],
          additionalEducation: educationResult.additionalEducation[surgeryId].additionalEducation
        };
      }
    });

    result.warnings = [...result.warnings, ...educationResult.warnings];
    result.recommendations = [...result.recommendations, ...educationResult.educationRecommendations];

    // 教育統計を統合
    result.dailyStats.educationStats = educationResult.educationStats;

    console.log(`✅ Phase 6完了: 教育配置=${educationResult.educationStats.totalEducationOpportunities}件, 追加配置=${educationResult.educationStats.additionalEducationAssignments}件`);

    // Phase 7: 外来人員配置
    console.log("🏥 Phase 7: 外来人員配置実行中...");
    const allUsedStaffIds = new Set([
      ...Array.from(basicAssignment.usedStaff),
      ...getAllEducationStaffIds(educationResult)
    ]);
    
    const outpatientAssignment = await assignOutpatientStaff(
      result.dailyStats.outpatientStaff,
      requirements.outpatientBreakdown,
      dailyStaff,
      allUsedStaffIds,
      eventParticipants,
      date
    );
    
    result.outpatientAssignments = outpatientAssignment.assignments;
    result.warnings = [...result.warnings, ...outpatientAssignment.warnings];
    console.log(`✅ Phase 7完了: 外来配置完了`);

    // Phase 8: 統計とメタデータ更新（教育考慮版）
    const assignedCount = countAssignedStaff(result.assignedSurgeries, result.outpatientAssignments, educationResult);
    result.dailyStats.assignedStaff = assignedCount;
    result.dailyStats.availableStaff = dailyStaff.length - assignedCount;
    result.dailyStats.unassignedStaff = getUnassignedStaff(dailyStaff, result.assignedSurgeries, result.outpatientAssignments, educationResult);
    
    // 🆕 教育活用率の計算
    result.dailyStats.educationStats.utilizationRate = (assignedCount / dailyStaff.length) * 100;

    // Phase 9: 修正提案の生成（教育重視版）
    result.recommendations = [...result.recommendations, ...generateEducationRecommendations(result.issues, result.warnings, result.dailyStats.educationStats)];

    // 🆕 Phase 10: 教育履歴の保存
    if (!debug) {
      console.log("💾 Phase 10: 教育履歴保存中...");
      await saveEducationHistory(date, {
        educationStats: result.dailyStats.educationStats,
        educationalAssignments: educationResult.educationalAssignments,
        additionalEducation: educationResult.additionalEducation
      });
      console.log("✅ Phase 10完了: 教育履歴保存完了");
    }

    console.log("🎉 教育重視版自動人員配置完了!");
    return result;

  } catch (error) {
    console.error("教育重視版配置システムエラー:", error);
    throw new Error(`教育重視版人員配置に失敗しました: ${error.message}`);
  }
};

// 🎓 包括的教育配置システム（Phase 6の完全置き換え）
const assignComprehensiveEducationalSupport = async (basicAssignments, roomAssignments, allStaff, usedStaff, eventParticipants, date) => {
  console.log("🎓 包括的教育配置システム開始...");
  
  const result = {
    educationalAssignments: {},
    additionalEducation: {},
    fullyUtilizedStaff: new Set(),
    educationStats: {
      totalEducationOpportunities: 0,
      observationAssignments: 0,
      guidedPracticeAssignments: 0,
      supervisionAssignments: 0,
      assessmentAssignments: 0,
      additionalEducationAssignments: 0,
      unusedStaffCount: 0
    },
    warnings: [],
    educationRecommendations: []
  };

  // STEP 1: 余剰人員の特定と分析
  const availableStaff = allStaff.filter(staff => !usedStaff.has(staff.employeeId));
  console.log(`👥 利用可能な教育要員: ${availableStaff.length}人`);

  if (availableStaff.length === 0) {
    result.warnings.push({
      type: "no_educational_capacity",
      message: "教育配置用の余剰人員がありません"
    });
    return result;
  }

  // STEP 2: 包括的教育ニーズ分析
  const educationNeeds = await analyzeComprehensiveEducationNeeds(basicAssignments, allStaff);
  console.log(`📊 特定された教育ニーズ: ${educationNeeds.length}件`);

  // STEP 3: 教育役割の分類と最適化
  const educationRoles = await classifyEducationalRoles(availableStaff, educationNeeds);
  console.log(`👨‍🏫 指導者: ${educationRoles.instructors.length}人, 🎓 学習者: ${educationRoles.learners.length}人, 🔄 多目的: ${educationRoles.multipurpose.length}人`);

  // STEP 4: 主要教育配置（高優先度）
  const primaryEducation = await assignEducationalStaff(
    educationNeeds.filter(need => need.priority === EDUCATION_PRIORITIES.HIGH),
    educationRoles,
    basicAssignments,
    eventParticipants,
    date
  );
  
  Object.assign(result.educationalAssignments, primaryEducation.assignments);
  result.educationStats.totalEducationOpportunities += Object.keys(primaryEducation.assignments).length;
  updateEducationStats(result.educationStats, primaryEducation.assignments);

  // STEP 5: セカンダリ教育配置（中優先度）
  const secondaryEducation = await assignEducationalStaff(
    educationNeeds.filter(need => need.priority === EDUCATION_PRIORITIES.MEDIUM),
    {
      instructors: primaryEducation.unusedStaff.filter(s => s.instructionCapability?.canInstruct),
      learners: primaryEducation.unusedStaff.filter(s => s.learningPotential?.canLearn),
      multipurpose: primaryEducation.unusedStaff.filter(s => s.flexibility)
    },
    basicAssignments,
    eventParticipants,
    date
  );
  
  Object.assign(result.educationalAssignments, secondaryEducation.assignments);
  result.educationStats.totalEducationOpportunities += Object.keys(secondaryEducation.assignments).length;
  updateEducationStats(result.educationStats, secondaryEducation.assignments);

  // STEP 6: 全余剰人員の追加教育配置
  const additionalEducation = await assignAllRemainingStaffForEducation(
    secondaryEducation.unusedStaff,
    basicAssignments,
    result.educationalAssignments,
    eventParticipants,
    date
  );
  
  Object.assign(result.additionalEducation, additionalEducation.assignments);
  result.educationStats.additionalEducationAssignments = Object.keys(additionalEducation.assignments).length;

  // STEP 7: 完全未配置人員の特別配置
  const finalAssignment = await handleCompletelyUnusedStaff(
    additionalEducation.remainingStaff,
    allStaff,
    basicAssignments,
    eventParticipants,
    date
  );
  
  result.educationStats.unusedStaffCount = finalAssignment.stillUnused.length;
  result.warnings.push(...finalAssignment.warnings);
  result.educationRecommendations.push(...finalAssignment.recommendations);

  // STEP 8: 教育効果分析と提案
  const educationAnalysis = analyzeEducationEffectiveness(result, allStaff, basicAssignments);
  result.educationRecommendations.push(...educationAnalysis.recommendations);

  console.log(`✅ 包括的教育配置完了: ${result.educationStats.totalEducationOpportunities}件の主要教育機会, ${result.educationStats.additionalEducationAssignments}件の追加配置`);
  
  return result;
};

// 🔄 全余剰人員の追加教育配置
const assignAllRemainingStaffForEducation = async (remainingStaff, basicAssignments, currentEducationAssignments, eventParticipants, date) => {
  const assignments = {};
  const warnings = [];
  
  console.log(`🔄 全余剰人員の教育配置: ${remainingStaff.length}人`);
  
  if (remainingStaff.length === 0) {
    return { assignments, remainingStaff: [], warnings };
  }
  
  // 全手術を教育機会として評価
  const allEducationOpportunities = await evaluateAllEducationOpportunities(
    basicAssignments,
    currentEducationAssignments,
    remainingStaff
  );
  
  console.log(`🎯 特定された追加教育機会: ${allEducationOpportunities.length}件`);
  
  // スタッフを教育価値順にソート
  const staffByValue = remainingStaff.map(staff => ({
    ...staff,
    totalEducationValue: calculateEducationValue(staff, allEducationOpportunities)
  })).sort((a, b) => b.totalEducationValue - a.totalEducationValue);
  
  const usedStaff = new Set();
  
  // 各教育機会に最適なスタッフを配置
  for (const opportunity of allEducationOpportunities) {
    const availableForThisOpportunity = staffByValue.filter(staff => 
      !usedStaff.has(staff.employeeId) && 
      !hasTimeConflict(staff, opportunity.surgery, eventParticipants)
    );
    
    if (availableForThisOpportunity.length === 0) continue;
    
    const assignment = await createOptimalEducationAssignment(
      opportunity,
      availableForThisOpportunity
    );
    
    if (assignment.success) {
      assignments[opportunity.surgeryId] = {
        ...basicAssignments[opportunity.surgeryId],
        additionalEducation: assignment.educationAssignment
      };
      
      // 使用済みスタッフを記録
      assignment.usedStaff.forEach(staff => {
        usedStaff.add(staff.employeeId);
      });
    }
  }
  
  const finalRemainingStaff = staffByValue.filter(staff => !usedStaff.has(staff.employeeId));
  
  console.log(`✅ 追加教育配置完了: ${Object.keys(assignments).length}件, 残余人員: ${finalRemainingStaff.length}人`);
  
  return { assignments, remainingStaff: finalRemainingStaff, warnings };
};

// 🎯 全教育機会の評価
const evaluateAllEducationOpportunities = async (basicAssignments, currentEducationAssignments, availableStaff) => {
  const opportunities = [];
  
  Object.entries(basicAssignments).forEach(([surgeryId, assignment]) => {
    // 既に教育配置がある手術はスキップ
    if (currentEducationAssignments[surgeryId]) return;
    
    const surgery = assignment.surgery;
    const educationValue = calculateSurgeryEducationValue(surgery);
    
    if (educationValue.hasEducationValue || canProvideGeneralEducation(surgery, assignment)) {
      opportunities.push({
        surgeryId,
        surgery,
        assignment,
        educationValue: educationValue.score,
        opportunities: identifyEducationOpportunities(surgery, assignment, availableStaff)
      });
    }
  });
  
  // 教育価値順にソート
  opportunities.sort((a, b) => b.educationValue - a.educationValue);
  
  return opportunities;
};

// 🎯 最適な教育配置の作成
const createOptimalEducationAssignment = async (opportunity, availableStaff) => {
  const surgery = opportunity.surgery;
  const assignment = opportunity.assignment;
  
  // 利用可能な教育タイプを評価
  const educationOptions = [
    evaluateObservationOpportunity(surgery, assignment, availableStaff),
    evaluateAssistanceOpportunity(surgery, assignment, availableStaff),
    evaluateSkillDevelopmentOpportunity(surgery, assignment, availableStaff),
    evaluateTeachingOpportunity(surgery, assignment, availableStaff)
  ].filter(option => option.feasible);
  
  if (educationOptions.length === 0) {
    return { success: false, reason: "適切な教育機会が見つかりません" };
  }
  
  // 最高価値の機会を選択
  educationOptions.sort((a, b) => b.educationValue - a.educationValue);
  const bestOption = educationOptions[0];
  
  return {
    success: true,
    educationAssignment: bestOption.assignment,
    usedStaff: bestOption.usedStaff
  };
};

// 🔍 各種教育機会の評価関数
const evaluateObservationOpportunity = (surgery, assignment, availableStaff) => {
  const observers = availableStaff.filter(staff => {
    const experience = staff.experienceYears || 0;
    if (experience > 3) return false;
    
    const deptSkills = staff.skills?.departments?.[surgery.department];
    if (!deptSkills) return true;
    
    const avgScore = ((deptSkills.scrubScore || 0) + (deptSkills.circulatingScore || 0)) / 2;
    return avgScore < 4;
  });
  
  if (observers.length === 0) {
    return { feasible: false };
  }
  
  const selectedObserver = observers[0];
  const educationValue = calculateObservationValue(selectedObserver, surgery);
  
  return {
    feasible: true,
    educationValue,
    assignment: {
      type: EDUCATION_TYPES.ADDITIONAL_OBSERVATION,
      observer: selectedObserver,
      purpose: `${surgery.procedure}の見学学習`,
      learningFocus: identifyLearningFocus(selectedObserver, surgery)
    },
    usedStaff: [selectedObserver]
  };
};

const evaluateAssistanceOpportunity = (surgery, assignment, availableStaff) => {
  const assistants = availableStaff.filter(staff => {
    const experience = staff.experienceYears || 0;
    const skillScore = staff.skillScore || 0;
    return experience >= 2 && experience <= 5 && skillScore >= 3.0;
  });
  
  if (assistants.length === 0) {
    return { feasible: false };
  }
  
  const selectedAssistant = assistants[0];
  const educationValue = calculateAssistanceValue(selectedAssistant, surgery);
  
  return {
    feasible: true,
    educationValue,
    assignment: {
      type: EDUCATION_TYPES.SURGICAL_ASSISTANCE,
      assistant: selectedAssistant,
      purpose: `${surgery.procedure}の補助業務`,
      skillDevelopment: identifyAssistanceSkills(selectedAssistant, surgery)
    },
    usedStaff: [selectedAssistant]
  };
};

const evaluateSkillDevelopmentOpportunity = (surgery, assignment, availableStaff) => {
  const developers = availableStaff.filter(staff => {
    return hasSkillDevelopmentNeed(staff, surgery);
  });
  
  if (developers.length === 0) {
    return { feasible: false };
  }
  
  const selectedDeveloper = developers[0];
  const educationValue = calculateSkillDevelopmentValue(selectedDeveloper, surgery);
  
  return {
    feasible: true,
    educationValue,
    assignment: {
      type: EDUCATION_TYPES.SKILL_DEVELOPMENT,
      developer: selectedDeveloper,
      purpose: `${surgery.procedure}での特定スキル向上`,
      targetSkills: identifyTargetSkills(selectedDeveloper, surgery)
    },
    usedStaff: [selectedDeveloper]
  };
};

const evaluateTeachingOpportunity = (surgery, assignment, availableStaff) => {
  const teachers = availableStaff.filter(staff => {
    const experience = staff.experienceYears || 0;
    const skillScore = staff.skillScore || 0;
    return experience >= 4 && skillScore >= 4.5;
  });
  
  if (teachers.length === 0) {
    return { feasible: false };
  }
  
  // 基本配置のスタッフが若手なら指導価値あり
  const hasJuniorStaff = 
    (assignment.scrub?.experienceYears || 10) <= 3 ||
    (assignment.circulating?.experienceYears || 10) <= 3;
  
  if (!hasJuniorStaff) {
    return { feasible: false };
  }
  
  const selectedTeacher = teachers[0];
  const educationValue = calculateTeachingValue(selectedTeacher, surgery, assignment);
  
  return {
    feasible: true,
    educationValue,
    assignment: {
      type: EDUCATION_TYPES.ADDITIONAL_TEACHING,
      teacher: selectedTeacher,
      purpose: `${surgery.procedure}での指導支援`,
      teachingFocus: identifyTeachingFocus(assignment)
    },
    usedStaff: [selectedTeacher]
  };
};

// 🚨 完全未配置人員の特別処理
const handleCompletelyUnusedStaff = async (unusedStaff, allStaff, basicAssignments, eventParticipants, date) => {
  const warnings = [];
  const recommendations = [];
  const stillUnused = [];
  
  console.log(`⚠️ 完全未配置人員の処理: ${unusedStaff.length}人`);
  
  if (unusedStaff.length === 0) {
    return { stillUnused: [], warnings, recommendations };
  }
  
  // 未配置理由の分析
  for (const staff of unusedStaff) {
    const analysisResult = analyzeWhyUnused(staff, basicAssignments, eventParticipants, date);
    
    warnings.push({
      type: "staff_unused",
      staffId: staff.employeeId,
      staffName: `${staff.lastName || ""} ${staff.firstName || ""}`.trim(),
      reason: analysisResult.reason,
      suggestions: analysisResult.suggestions
    });
    
    // 改善提案の生成
    if (analysisResult.canImprove) {
      recommendations.push({
        type: "utilization_improvement",
        target: staff.employeeId,
        staffName: `${staff.lastName || ""} ${staff.firstName || ""}`.trim(),
        recommendation: analysisResult.improvementSuggestion
      });
    }
    
    stillUnused.push(staff);
  }
  
  // 全体的な改善提案
  if (unusedStaff.length > 2) {
    recommendations.push({
      type: "system_improvement",
      recommendation: "手術スケジュールの調整や教育プログラムの見直しを検討してください",
      details: {
        unusedCount: unusedStaff.length,
        totalStaff: allStaff.length,
        utilizationRate: ((allStaff.length - unusedStaff.length) / allStaff.length * 100).toFixed(1)
      }
    });
  }
  
  return { stillUnused, warnings, recommendations };
};

// 📊 教育効果分析
const analyzeEducationEffectiveness = (educationResult, allStaff, basicAssignments) => {
  const recommendations = [];
  
  const totalStaff = allStaff.length;
  const educatedStaff = educationResult.educationStats.totalEducationOpportunities + 
                       educationResult.educationStats.additionalEducationAssignments;
  const utilizationRate = (educatedStaff / totalStaff) * 100;
  
  // 活用率の評価
  if (utilizationRate < 70) {
    recommendations.push({
      type: "improve_utilization",
      message: `スタッフ活用率が${utilizationRate.toFixed(1)}%と低めです。教育機会の拡大を検討してください。`,
      priority: "medium"
    });
  } else if (utilizationRate > 95) {
    recommendations.push({
      type: "excellent_utilization",
      message: `スタッフ活用率${utilizationRate.toFixed(1)}%で優秀です。この状態を維持してください。`,
      priority: "info"
    });
  }
  
  // 教育バランスの評価
  const stats = educationResult.educationStats;
  const totalEducation = stats.observationAssignments + stats.guidedPracticeAssignments + 
                        stats.supervisionAssignments + stats.assessmentAssignments;
  
  if (totalEducation > 0) {
    const observationRatio = stats.observationAssignments / totalEducation;
    const practiceRatio = stats.guidedPracticeAssignments / totalEducation;
    
    if (observationRatio > 0.6) {
      recommendations.push({
        type: "balance_education",
        message: "見学配置が多めです。実践的な教育機会の増加を検討してください。",
        priority: "low"
      });
    }
    
    if (practiceRatio < 0.2) {
      recommendations.push({
        type: "increase_practice",
        message: "実践的な教育配置が少ないです。指導下実践の機会を増やしてください。",
        priority: "medium"
      });
    }
  }
  
  return { recommendations };
};

// 🔄 既存の関数群を保持（変更なし）
const getDailyStaffWithEvents = async (date) => {
  try {
    const dailyStaff = await getDailyStaffList(date);
    const eventParticipants = await getEventParticipantsWithTimeSlots(date);
    return { dailyStaff, eventParticipants };
  } catch (error) {
    console.error("日勤者リスト取得エラー:", error);
    return { dailyStaff: [], eventParticipants: [] };
  }
};

const getEventParticipantsWithTimeSlots = async (date) => {
  try {
    const eventQuery = query(
      collection(db, "events"),
      where("date", "==", date)
    );
    const eventSnap = await getDocs(eventQuery);
    
    const participantsWithTimeSlots = [];
    
    eventSnap.docs.forEach(doc => {
      const eventData = doc.data();
      const eventParticipants = eventData.participants || [];
      
      eventParticipants.forEach(empId => {
        participantsWithTimeSlots.push({
          employeeId: empId,
          eventId: doc.id,
          eventTitle: eventData.title || "イベント",
          startTime: eventData.startTime || "00:00",
          endTime: eventData.endTime || "23:59",
          unavailableSlots: generateUnavailableTimeSlots(
            eventData.startTime || "00:00", 
            eventData.endTime || "23:59"
          )
        });
      });
    });
    
    return participantsWithTimeSlots;
  } catch (error) {
    console.error("イベント参加者取得エラー:", error);
    return [];
  }
};

const generateUnavailableTimeSlots = (startTime, endTime) => {
  const slots = [];
  const start = dayjs(`2024-01-01 ${startTime}`);
  const end = dayjs(`2024-01-01 ${endTime}`);
  
  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    slots.push(current.format("HH:mm"));
    current = current.add(15, "minute");
  }
  
  return slots;
};

const calculateStaffRequirements = async (date) => {
  try {
    const outpatientRequirement = await getOutpatientStaffRequirement(date);
    const surgeries = await getSurgeryScheduleForDate(date);
    const concurrencyAnalysis = calculateMaxConcurrentSurgeries(surgeries);
    
    const totalRequired = outpatientRequirement.total + concurrencyAnalysis.maxRequiredStaff;
    
    return {
      outpatientStaff: outpatientRequirement.total,
      outpatientBreakdown: outpatientRequirement.breakdown,
      surgeries,
      maxConcurrentSurgeries: concurrencyAnalysis.maxConcurrentCount,
      maxRequiredStaff: concurrencyAnalysis.maxRequiredStaff,
      totalRequired,
      concurrencyDetails: concurrencyAnalysis.details
    };
  } catch (error) {
    console.error("スタッフ必要数計算エラー:", error);
    return {
      outpatientStaff: 5,
      surgeries: [],
      maxConcurrentSurgeries: 0,
      maxRequiredStaff: 0,
      totalRequired: 5
    };
  }
};

const getOutpatientStaffRequirement = async (date) => {
  try {
    const outpatientDoc = await getDoc(doc(db, "systemSettings", "outpatientSettings"));
    
    if (!outpatientDoc.exists()) {
      return {
        total: 5,
        breakdown: { leader: 1, member: 4 }
      };
    }
    
    const settings = outpatientDoc.data();
    const dayOfWeek = dayjs(date).day();
    
    if (settings.specialDates && settings.specialDates[date]) {
      const special = settings.specialDates[date];
      return {
        total: (special.leader || 0) + (special.member || 0),
        breakdown: special
      };
    }
    
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const pattern = isWeekend ? settings.patterns.weekend : settings.patterns.weekday;
    
    return {
      total: (pattern.leader || 0) + (pattern.member || 0),
      breakdown: pattern
    };
  } catch (error) {
    console.error("術前外来必要人数取得エラー:", error);
    return { total: 5, breakdown: { leader: 1, member: 4 } };
  }
};

const getSurgeryScheduleForDate = async (date) => {
  try {
    const surgeryQuery = query(
      collection(db, "surgerySchedules"),
      where("date", "==", date)
    );
    const surgerySnap = await getDocs(surgeryQuery);
    
    return surgerySnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      requiredSkills: {
        scrub: getRequiredSkillLevel(doc.data(), "scrub"),
        circulating: getRequiredSkillLevel(doc.data(), "circulating")
      }
    }));
  } catch (error) {
    console.error("手術スケジュール取得エラー:", error);
    return [];
  }
};

const calculateMaxConcurrentSurgeries = (surgeries) => {
  if (surgeries.length === 0) {
    return { maxConcurrentCount: 0, maxRequiredStaff: 0, details: [] };
  }
  
  const timeSlots = {};
  
  surgeries.forEach(surgery => {
    const startTime = dayjs(`2024-01-01 ${surgery.start}`);
    const endTime = dayjs(`2024-01-01 ${surgery.end}`);
    
    let current = startTime;
    while (current.isBefore(endTime)) {
      const timeKey = current.format("HH:mm");
      if (!timeSlots[timeKey]) {
        timeSlots[timeKey] = [];
      }
      timeSlots[timeKey].push({
        ...surgery,
        requiredStaff: calculateSurgeryRequiredStaff(surgery)
      });
      current = current.add(15, "minute");
    }
  });
  
  let maxConcurrentCount = 0;
  let maxRequiredStaff = 0;
  const details = [];
  
  Object.entries(timeSlots).forEach(([time, concurrentSurgeries]) => {
    const concurrentCount = concurrentSurgeries.length;
    const requiredStaff = concurrentSurgeries.reduce((sum, surgery) => sum + surgery.requiredStaff, 0);
    
    if (concurrentCount > maxConcurrentCount) {
      maxConcurrentCount = concurrentCount;
    }
    
    if (requiredStaff > maxRequiredStaff) {
      maxRequiredStaff = requiredStaff;
    }
    
    if (concurrentCount > 1) {
      details.push({
        time,
        count: concurrentCount,
        requiredStaff,
        surgeries: concurrentSurgeries.map(s => ({
          room: s.room,
          procedure: s.procedure,
          requiredStaff: s.requiredStaff
        }))
      });
    }
  });
  
  return { maxConcurrentCount, maxRequiredStaff, details };
};

const calculateSurgeryRequiredStaff = (surgery) => {
  let required = 2;
  
  const difficulty = surgery.difficulty || "normal";
  const procedure = surgery.procedure || "";
  
  if (difficulty === "high" || procedure.includes("開心術") || procedure.includes("脳外科")) {
    required += 1;
  }
  
  const duration = calculateSurgeryDuration(surgery);
  if (duration >= 180) {
    required += 1;
  }
  
  return required;
};

const calculateSurgeryDuration = (surgery) => {
  if (!surgery.start || !surgery.end) return 60;
  
  const start = dayjs(`2024-01-01 ${surgery.start}`);
  const end = dayjs(`2024-01-01 ${surgery.end}`);
  
  return end.diff(start, "minute");
};

const sortSurgeriesByPriorityAndDifficulty = async (surgeries) => {
  try {
    const surgeriesWithPriority = await Promise.all(
      surgeries.map(async (surgery) => {
        const priorityScore = await calculateSurgeryPriorityScore(surgery);
        return { ...surgery, priorityScore };
      })
    );
    
    return surgeriesWithPriority.sort((a, b) => b.priorityScore - a.priorityScore);
  } catch (error) {
    console.error("手術優先度ソートエラー:", error);
    return surgeries;
  }
};

const calculateSurgeryPriorityScore = async (surgery) => {
  let score = 50;
  
  if (surgery.priority === "emergency") score += 100;
  else if (surgery.priority === "urgent") score += 50;
  else if (surgery.priority === "high") score += 30;
  
  const procedure = surgery.procedure || "";
  const difficultyKeywords = {
    high: ["開心術", "脳腫瘍", "肝切除", "膵頭十二指腸切除", "食道切除"],
    medium: ["腹腔鏡", "内視鏡", "関節鏡", "胆嚢摘出", "虫垂切除"],
    low: ["皮膚", "痔核", "ヘルニア", "生検"]
  };
  
  if (difficultyKeywords.high.some(keyword => procedure.includes(keyword))) {
    score += 40;
  } else if (difficultyKeywords.medium.some(keyword => procedure.includes(keyword))) {
    score += 20;
  } else if (difficultyKeywords.low.some(keyword => procedure.includes(keyword))) {
    score += 10;
  }
  
  const duration = calculateSurgeryDuration(surgery);
  if (duration >= 300) score += 30;
  else if (duration >= 180) score += 20;
  else if (duration >= 120) score += 10;
  
  const department = surgery.department || "";
  const highRiskDepartments = ["心臓外科", "脳神経外科", "救急科"];
  if (highRiskDepartments.includes(department)) {
    score += 25;
  }
  
  return Math.max(0, Math.min(200, score));
};

const assignBasicStaffToSurgeries = async (surgeries, experienced, trainees, eventParticipants, date) => {
  const assignments = {};
  const roomAssignments = {};
  const usedStaff = new Set();
  const issues = [];
  const warnings = [];
  
  const compatibilityData = await getCompatibilityData();
  const surgeriesByRoom = groupSurgeriesByRoom(surgeries);
  
  for (const [room, roomSurgeries] of Object.entries(surgeriesByRoom)) {
    console.log(`🏥 処理中: ${room} (${roomSurgeries.length}件の手術)`);
    
    const roomStaffAssignment = await selectOptimalRoomStaff(
      room,
      roomSurgeries,
      experienced,
      trainees,
      usedStaff,
      eventParticipants,
      compatibilityData,
      date
    );
    
    if (roomStaffAssignment.success) {
      roomAssignments[room] = roomStaffAssignment.roomStaff;
      
      roomStaffAssignment.roomStaff.forEach(staff => {
        usedStaff.add(staff.employeeId);
      });
      
      roomSurgeries.forEach(surgery => {
        assignments[surgery.id] = {
          surgery,
          scrub: roomStaffAssignment.roomStaff.find(s => s.role === "scrub") || null,
          circulating: roomStaffAssignment.roomStaff.find(s => s.role === "circulating") || null,
          room: room,
          isRoomBasedAssignment: true
        };
      });
      
      console.log(`✅ ${room}配置完了: ${roomStaffAssignment.roomStaff.map(s => s.name).join(", ")}`);
    } else {
      issues.push({
        type: "room_assignment_failed",
        room: room,
        surgeryCount: roomSurgeries.length,
        message: `${room}にスタッフを配置できませんでした`,
        reason: roomStaffAssignment.reason
      });
      
      console.warn(`⚠️ ${room}配置失敗: ${roomStaffAssignment.reason}`);
    }
  }
  
  return { assignments, roomAssignments, usedStaff, issues, warnings };
};

const groupSurgeriesByRoom = (surgeries) => {
  const grouped = {};
  
  surgeries.forEach(surgery => {
    const room = surgery.room || "未定";
    if (!grouped[room]) {
      grouped[room] = [];
    }
    grouped[room].push(surgery);
  });
  
  return grouped;
};

const selectOptimalRoomStaff = async (room, roomSurgeries, experienced, trainees, usedStaff, eventParticipants, compatibilityData, date) => {
  const availableStaff = [...experienced, ...trainees].filter(staff => {
    if (usedStaff.has(staff.employeeId)) return false;
    
    const isUnavailable = eventParticipants.some(event => {
      if (event.employeeId !== staff.employeeId) return false;
      
      return roomSurgeries.some(surgery => {
        return isTimeOverlap(
          surgery.start, surgery.end,
          event.startTime, event.endTime
        );
      });
    });
    
    return !isUnavailable;
  });
  
  if (availableStaff.length < 2) {
    return {
      success: false,
      reason: `利用可能スタッフ不足 (必要2名、利用可能${availableStaff.length}名)`
    };
  }
  
  const maxDifficultyScore = Math.max(...roomSurgeries.map(s => s.priorityScore || 50));
  const skillRequirement = maxDifficultyScore > 120 ? "high" : maxDifficultyScore > 80 ? "medium" : "low";
  
  const candidatePairs = [];
  
  for (let i = 0; i < availableStaff.length; i++) {
    for (let j = i + 1; j < availableStaff.length; j++) {
      const staff1 = availableStaff[i];
      const staff2 = availableStaff[j];
      
      const isCompatible = checkStaffCompatibility(staff1, staff2, compatibilityData);
      if (!isCompatible) continue;
      
      const both1stYear = (staff1.experienceYears || 0) <= 1 && (staff2.experienceYears || 0) <= 1;
      if (both1stYear && skillRequirement !== "low") continue;
      
      const pattern1 = evaluateStaffPattern(staff1, staff2, roomSurgeries, "scrub", "circulating");
      const pattern2 = evaluateStaffPattern(staff2, staff1, roomSurgeries, "scrub", "circulating");
      
      candidatePairs.push(pattern1, pattern2);
    }
  }
  
  if (candidatePairs.length === 0) {
    return {
      success: false,
      reason: "適切なスタッフペアが見つかりませんでした"
    };
  }
  
  candidatePairs.sort((a, b) => b.score - a.score);
  const bestPair = candidatePairs[0];
  
  return {
    success: true,
    roomStaff: [
      { ...bestPair.scrub, role: "scrub" },
      { ...bestPair.circulating, role: "circulating" }
    ],
    score: bestPair.score
  };
};

const checkStaffCompatibility = (staff1, staff2, compatibilityData) => {
  return !compatibilityData.some(data => 
    (data.userA === staff1.employeeId && data.userB === staff2.employeeId) ||
    (data.userA === staff2.employeeId && data.userB === staff1.employeeId)
  );
};

const evaluateStaffPattern = (scrubStaff, circulatingStaff, surgeries, scrubRole, circulatingRole) => {
  let score = 0;
  
  const avgRequiredScrubSkill = surgeries.reduce((sum, s) => sum + (s.requiredSkills?.scrub || 2), 0) / surgeries.length;
  const avgRequiredCirculatingSkill = surgeries.reduce((sum, s) => sum + (s.requiredSkills?.circulating || 2), 0) / surgeries.length;
  
  const scrubSkill = getStaffSkillForRole(scrubStaff, surgeries[0], scrubRole);
  const circulatingSkill = getStaffSkillForRole(circulatingStaff, surgeries[0], circulatingRole);
  
  if (scrubSkill >= avgRequiredScrubSkill) score += 20;
  if (circulatingSkill >= avgRequiredCirculatingSkill) score += 20;
  
  score += Math.min(10, (scrubStaff.experienceYears || 0) * 2);
  score += Math.min(10, (circulatingStaff.experienceYears || 0) * 2);
  
  const experienceDiff = Math.abs((scrubStaff.experienceYears || 0) - (circulatingStaff.experienceYears || 0));
  if (experienceDiff <= 2) score += 10;
  
  return {
    scrub: scrubStaff,
    circulating: circulatingStaff,
    score: score
  };
};

const assignOutpatientStaff = async (requiredCount, breakdown, allStaff, usedStaffIds, eventParticipants, date) => {
  const outpatientAssignments = {};
  const warnings = [];
  
  const availableStaff = allStaff.filter(staff => {
    if (usedStaffIds instanceof Set) {
      return !usedStaffIds.has(staff.employeeId);
    } else if (Array.isArray(usedStaffIds)) {
      return !usedStaffIds.includes(staff.employeeId);
    } else {
      console.warn("usedStaffIds の型が不正です:", typeof usedStaffIds);
      return true;
    }
  });
  
  console.log(`🏥 外来配置: 必要=${requiredCount}人, 利用可能=${availableStaff.length}人`);
  
  if (availableStaff.length < requiredCount) {
    warnings.push({
      type: "insufficient_outpatient_staff",
      message: `外来に必要な人数(${requiredCount}人)に対して利用可能スタッフが不足しています(${availableStaff.length}人)`
    });
  }
  
  const staffWithPriority = availableStaff.map(staff => ({
    ...staff,
    outpatientPriority: calculateOutpatientPriority(staff)
  })).sort((a, b) => b.outpatientPriority - a.outpatientPriority);
  
  const roles = Object.keys(breakdown);
  let staffIndex = 0;
  
  for (const role of roles) {
    const count = breakdown[role] || 0;
    outpatientAssignments[role] = [];
    
    for (let i = 0; i < count && staffIndex < staffWithPriority.length; i++) {
      const staff = staffWithPriority[staffIndex];
      
      const hasTimeConflict = eventParticipants.some(event => {
        if (event.employeeId !== staff.employeeId) return false;
        return isTimeOverlap("08:30", "17:00", event.startTime, event.endTime);
      });
      
      if (!hasTimeConflict) {
        outpatientAssignments[role].push({
          ...staff,
          assignedRole: role,
          assignedArea: "外来"
        });
        staffIndex++;
      } else {
        const nextAvailable = staffWithPriority.slice(staffIndex + 1).find(nextStaff => {
          return !eventParticipants.some(event => {
            if (event.employeeId !== nextStaff.employeeId) return false;
            return isTimeOverlap("08:30", "17:00", event.startTime, event.endTime);
          });
        });
        
        if (nextAvailable) {
          const nextIndex = staffWithPriority.indexOf(nextAvailable);
          outpatientAssignments[role].push({
            ...nextAvailable,
            assignedRole: role,
            assignedArea: "外来"
          });
          staffWithPriority.splice(nextIndex, 1);
        } else {
          warnings.push({
            type: "outpatient_time_conflict",
            message: `${role}に配置できるスタッフが時間的制約により不足しています`
          });
        }
      }
    }
  }
  
  console.log(`✅ 外来配置完了: ${Object.values(outpatientAssignments).flat().length}人を配置`);
  
  return { assignments: outpatientAssignments, warnings };
};

const calculateOutpatientPriority = (staff) => {
  let priority = 50;
  
  const experience = staff.experienceYears || 0;
  if (experience >= 3 && experience <= 7) {
    priority += 30;
  } else if (experience >= 8) {
    priority += 20;
  } else if (experience >= 2) {
    priority += 15;
  } else {
    priority += 5;
  }
  
  const skillScore = staff.skillScore || 0;
  priority += Math.min(20, skillScore * 4);
  
  return priority;
};

const getDailyStaffList = async (date) => {
  try {
    const year = dayjs(date).year();
    const month = dayjs(date).month() + 1;
    const monthTitle = `${year}年${month}月`;
    
    const shiftDoc = await getDoc(doc(db, "shiftSchedules", monthTitle));
    if (!shiftDoc.exists()) {
      console.warn(`${monthTitle}のシフトデータが存在しません`);
      return [];
    }
    
    const shiftData = shiftDoc.data();
    
    const staffSnap = await getDocs(collection(db, "staffList"));
    const allStaff = staffSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const promises = [];
    
    for (const staff of allStaff) {
      const employeeId = staff.employeeId;
      if (!employeeId) continue;
      
      const staffShifts = shiftData[employeeId] || {};
      const todayShift = staffShifts[date] || "";

      // Phase F: 「空 = 日勤」を廃止。明示的に "◯" が入っているレコードのみ日勤として扱う。
      // ※ この関数は Firebase 由来の旧データに依存しているため、本来は monthly_shifts +
      //    shift_patterns.configuration.assignmentModeEnabled を見るのが正だが、
      //    呼び出し元（AutoStaffAssignmentModal）は移行待ち。当面は安全側で "◯" のみを拾う。
      if (todayShift === "◯") {
        promises.push(
          getStaffSkills(staff.id).then(skillData => {
            const skillScore = calculateSkillScore(skillData);
            const experienceYears = calculateExperienceYears(staff);
            
            return {
              ...staff,
              skills: skillData,
              skillScore,
              experienceYears
            };
          })
        );
      }
    }
    
    return await Promise.all(promises);
  } catch (error) {
    console.error("日勤者リスト取得エラー:", error);
    return [];
  }
};

const getCompatibilityData = async () => {
  try {
    const compatibilitySnap = await getDocs(collection(db, "shiftIncompatibilities"));
    return compatibilitySnap.docs.map(doc => doc.data());
  } catch (error) {
    console.warn("相性データ取得エラー:", error);
    return [];
  }
};

const calculateSkillScore = (skillData) => {
  if (!skillData || Object.keys(skillData).length === 0) {
    return 0;
  }
  
  const departments = skillData.departments || {};
  let totalScore = 0;
  let count = 0;
  
  Object.values(departments).forEach(dept => {
    if (dept.averageScore) {
      totalScore += dept.averageScore;
      count++;
    }
  });
  
  return count > 0 ? totalScore / count : 0;
};

const getRequiredSkillLevel = (surgery, role) => {
  const difficulty = surgery.difficulty || "normal";
  
  if (difficulty === "high") {
    return role === "scrub" ? 5 : 3;
  } else if (difficulty === "normal") {
    return role === "scrub" ? 3 : 2;
  } else {
    return role === "scrub" ? 2 : 1;
  }
};

const getStaffSkills = async (staffId) => {
  try {
    const skillDoc = await getDoc(doc(db, "staffSkillSummary", staffId));
    if (!skillDoc.exists()) {
      return {};
    }
    return skillDoc.data();
  } catch (error) {
    console.error(`スキルデータ取得エラー (${staffId}):`, error);
    return {};
  }
};

const classifyStaffBySkill = (staffList) => {
  const SKILL_THRESHOLD = 3.5;
  
  const experienced = [];
  const trainees = [];
  
  for (const staff of staffList) {
    const skillScore = staff.skillScore || 0;
    const experienceYears = staff.experienceYears || 0;
    
    if (skillScore >= SKILL_THRESHOLD || experienceYears >= 3) {
      experienced.push(staff);
    } else {
      trainees.push(staff);
    }
  }
  
  trainees.sort((a, b) => {
    const expA = a.experienceYears || 0;
    const expB = b.experienceYears || 0;
    
    if (expA !== expB) {
      return expA - expB;
    }
    
    return (a.skillScore || 0) - (b.skillScore || 0);
  });
  
  return { experienced, trainees };
};

const getStaffSkillForRole = (staff, surgery, role) => {
  if (!staff.skills || !staff.skills.departments) {
    return 0;
  }
  
  const department = staff.skills.departments[surgery.department || "未分類"];
  if (!department) {
    return 0;
  }
  
  if (role === "scrub") {
    return department.scrubScore || 0;
  } else if (role === "circulating") {
    return department.circulatingScore || 0;
  }
  
  return department.averageScore || 0;
};

const generateEducationRecommendations = (issues, warnings, educationStats) => {
  const recommendations = [];
  
  issues.forEach(issue => {
    if (issue.type === "room_assignment_failed") {
      recommendations.push({
        type: "increase_staff",
        target: issue.room,
        message: `${issue.room}の配置失敗: スタッフ数の増員を検討してください`
      });
    } else if (issue.type === "missing_staff") {
      recommendations.push({
        type: "add_staff",
        target: issue.surgeryId,
        role: issue.role,
        message: `${issue.role === "scrub" ? "器械出し" : "外回り"}の追加配置が必要です`
      });
    }
  });
  
  warnings.forEach(warning => {
    if (warning.type === "no_educational_capacity") {
      recommendations.push({
        type: "optimize_schedule",
        message: "教育機会の確保のため、スケジュール調整を検討してください"
      });
    }
  });
  
  // 🆕 教育関連の推奨事項
  if (educationStats.utilizationRate < 70) {
    recommendations.push({
      type: "improve_education_utilization",
      message: `教育活用率が${educationStats.utilizationRate.toFixed(1)}%です。より多くの教育機会を創出できる可能性があります。`
    });
  }
  
  if (educationStats.totalEducationOpportunities === 0) {
    recommendations.push({
      type: "create_education_opportunities",
      message: "教育機会が全く作られていません。スタッフの成長機会を増やすことを検討してください。"
    });
  }
  
  return recommendations;
};

// 🆕 教育配置で使用されたスタッフIDを取得
const getAllEducationStaffIds = (educationResult) => {
  const staffIds = [];
  
  // 教育配置のスタッフ
  Object.values(educationResult.educationalAssignments || {}).forEach(assignment => {
    const educationAssignment = assignment.educationAssignment;
    if (educationAssignment) {
      if (educationAssignment.instructor) staffIds.push(educationAssignment.instructor.employeeId);
      if (educationAssignment.observer) staffIds.push(educationAssignment.observer.employeeId);
      if (educationAssignment.supervisor) staffIds.push(educationAssignment.supervisor.employeeId);
      if (educationAssignment.evaluator) staffIds.push(educationAssignment.evaluator.employeeId);
      if (educationAssignment.trainee) staffIds.push(educationAssignment.trainee.employeeId);
      if (educationAssignment.supervisee) staffIds.push(educationAssignment.supervisee.employeeId);
      if (educationAssignment.candidate) staffIds.push(educationAssignment.candidate.employeeId);
    }
  });
  
  // 追加教育配置のスタッフ
  Object.values(educationResult.additionalEducation || {}).forEach(assignment => {
    const additionalEducation = assignment.additionalEducation;
    if (additionalEducation) {
      if (additionalEducation.observer) staffIds.push(additionalEducation.observer.employeeId);
      if (additionalEducation.assistant) staffIds.push(additionalEducation.assistant.employeeId);
      if (additionalEducation.developer) staffIds.push(additionalEducation.developer.employeeId);
      if (additionalEducation.teacher) staffIds.push(additionalEducation.teacher.employeeId);
    }
  });
  
  return staffIds;
};

const countAssignedStaff = (assignments, outpatientAssignments = {}, educationResult = {}) => {
  const assignedIds = new Set();
  
  // 手術配置のスタッフ
  for (const surgeryId in assignments) {
    const assignment = assignments[surgeryId];
    if (assignment.scrub) {
      assignedIds.add(assignment.scrub.employeeId);
    }
    if (assignment.circulating) {
      assignedIds.add(assignment.circulating.employeeId);
    }
  }
  
  // 教育配置のスタッフ
  getAllEducationStaffIds(educationResult).forEach(staffId => {
    assignedIds.add(staffId);
  });
  
  // 外来配置のスタッフ
  for (const role in outpatientAssignments) {
    const roleStaff = outpatientAssignments[role] || [];
    roleStaff.forEach(staff => {
      if (staff.employeeId) {
        assignedIds.add(staff.employeeId);
      }
    });
  }
  
  return assignedIds.size;
};

const getUnassignedStaff = (dailyStaff, assignments, outpatientAssignments = {}, educationResult = {}) => {
  const assignedIds = new Set();
  
  // 手術配置のスタッフ
  for (const surgeryId in assignments) {
    const assignment = assignments[surgeryId];
    if (assignment.scrub) {
      assignedIds.add(assignment.scrub.employeeId);
    }
    if (assignment.circulating) {
      assignedIds.add(assignment.circulating.employeeId);
    }
  }
  
  // 教育配置のスタッフ
  getAllEducationStaffIds(educationResult).forEach(staffId => {
    assignedIds.add(staffId);
  });
  
  // 外来配置のスタッフ
  for (const role in outpatientAssignments) {
    const roleStaff = outpatientAssignments[role] || [];
    roleStaff.forEach(staff => {
      if (staff.employeeId) {
        assignedIds.add(staff.employeeId);
      }
    });
  }
  
  return dailyStaff
    .filter(staff => !assignedIds.has(staff.employeeId))
    .map(staff => ({
      id: staff.id,
      employeeId: staff.employeeId,
      name: `${staff.lastName || ""} ${staff.firstName || ""}`.trim(),
      experienceYears: staff.experienceYears || 0,
      skillScore: staff.skillScore || 0
    }));
};

const calculateExperienceYears = (staff) => {
  if (!staff || !staff.year) return 0;
  
  const entryYear = parseInt(staff.year);
  const currentYear = new Date().getFullYear();
  
  return currentYear - entryYear;
};

// 🔄 補助関数群（教育評価用）
const calculateSurgeryEducationValue = (surgery) => {
  let score = 0;
  
  const procedure = surgery.procedure || "";
  const department = surgery.department || "";
  
  if (procedure.includes("開心術") || procedure.includes("脳腫瘍")) {
    score += 40;
  }
  
  if (procedure.includes("腹腔鏡") || procedure.includes("ロボット")) {
    score += 30;
  }
  
  const duration = calculateSurgeryDuration(surgery);
  if (duration >= 120 && duration <= 300) {
    score += 20;
  }
  
  const highEducationDepts = ["心臓外科", "脳神経外科", "整形外科"];
  if (highEducationDepts.includes(department)) {
    score += 25;
  }
  
  return {
    hasEducationValue: score >= 30,
    score
  };
};

const canProvideGeneralEducation = (surgery, assignment) => {
  const hasJuniorStaff = 
    (assignment.scrub?.experienceYears || 10) <= 3 ||
    (assignment.circulating?.experienceYears || 10) <= 3;
  
  return hasJuniorStaff;
};

const identifyEducationOpportunities = (surgery, assignment, availableStaff) => {
  const opportunities = [];
  
  const hasObservationValue = availableStaff.some(staff => (staff.experienceYears || 0) <= 2);
  if (hasObservationValue) {
    opportunities.push("observation");
  }
  
  const hasAssistanceValue = availableStaff.some(staff => {
    const exp = staff.experienceYears || 0;
    return exp >= 2 && exp <= 5;
  });
  if (hasAssistanceValue) {
    opportunities.push("assistance");
  }
  
  return opportunities;
};

const hasTimeConflict = (staff, surgery, eventParticipants) => {
  return eventParticipants.some(event => {
    if (event.employeeId !== staff.employeeId) return false;
    return isTimeOverlap(surgery.start, surgery.end, event.startTime, event.endTime);
  });
};

const updateEducationStats = (stats, assignments) => {
  Object.values(assignments).forEach(assignment => {
    if (assignment.educationAssignment) {
      const type = assignment.educationAssignment.type;
      
      if (type.includes("observation")) {
        stats.observationAssignments++;
      } else if (type.includes("guided_practice")) {
        stats.guidedPracticeAssignments++;
      } else if (type.includes("supervision")) {
        stats.supervisionAssignments++;
      } else if (type.includes("assessment")) {
        stats.assessmentAssignments++;
      }
    }
  });
};

const analyzeWhyUnused = (staff, basicAssignments, eventParticipants, date) => {
  const reasons = [];
  const suggestions = [];
  let canImprove = false;
  
  const eventConflicts = eventParticipants.filter(event => 
    event.employeeId === staff.employeeId
  );
  
  if (eventConflicts.length > 0) {
    reasons.push("イベント参加による時間制約");
    suggestions.push("イベントスケジュールの調整を検討");
  }
  
  const skillScore = staff.skillScore || 0;
  if (skillScore < 2.0) {
    reasons.push("基本スキル不足");
    suggestions.push("基礎教育プログラムの実施");
    canImprove = true;
  }
  
  const experienceYears = staff.experienceYears || 0;
  if (experienceYears < 1) {
    reasons.push("経験不足による安全性考慮");
    suggestions.push("段階的な教育プログラムの導入");
    canImprove = true;
  }
  
  const surgeryDifficulties = Object.values(basicAssignments).map(a => a.surgery.difficulty);
  const highDifficultyCount = surgeryDifficulties.filter(d => d === "high").length;
  
  if (highDifficultyCount > 0 && skillScore < 4.0) {
    reasons.push("高難度手術が多く、現在のスキルレベルでは配置困難");
    suggestions.push("中難度手術での経験積み重ねが必要");
    canImprove = true;
  }
  
  const mainReason = reasons.length > 0 ? reasons[0] : "配置機会の不足";
  const improvementSuggestion = suggestions.length > 0 ? suggestions.join(", ") : "継続的なスキル向上";
  
  return {
    reason: mainReason,
    suggestions,
    canImprove,
    improvementSuggestion
  };
};

// 各種価値計算関数
const calculateObservationValue = (observer, surgery) => {
  let value = 30;
  
  const experience = observer.experienceYears || 0;
  if (experience <= 1) value += 20;
  
  const deptSkills = observer.skills?.departments?.[surgery.department];
  if (!deptSkills) value += 15;
  
  return value;
};

const calculateAssistanceValue = (assistant, surgery) => {
  let value = 25;
  
  const skillScore = assistant.skillScore || 0;
  if (skillScore < 4.0) value += 15;
  
  return value;
};

const calculateSkillDevelopmentValue = (developer, surgery) => {
  return 35;
};

const calculateTeachingValue = (teacher, surgery, assignment) => {
  let value = 40;
  
  const juniorCount = [assignment.scrub, assignment.circulating]
    .filter(staff => staff && (staff.experienceYears || 0) <= 2).length;
  
  value += juniorCount * 10;
  
  return value;
};

const hasSkillDevelopmentNeed = (staff, surgery) => {
  const deptSkills = staff.skills?.departments?.[surgery.department];
  if (!deptSkills) return false;
  
  const avgScore = ((deptSkills.scrubScore || 0) + (deptSkills.circulatingScore || 0)) / 2;
  return avgScore >= 2 && avgScore < 5;
};

const identifyLearningFocus = (observer, surgery) => {
  return [`${surgery.procedure}の流れ`, "使用器械", "看護師の役割"];
};

const identifyAssistanceSkills = (assistant, surgery) => {
  return ["器械準備", "術野管理", "チームワーク"];
};

const identifyTargetSkills = (developer, surgery) => {
  return [`${surgery.department}特有の技術`, "高度な器械操作"];
};

const identifyTeachingFocus = (assignment) => {
  const focuses = [];
  if (assignment.scrub && (assignment.scrub.experienceYears || 0) <= 2) {
    focuses.push("器械出し指導");
  }
  if (assignment.circulating && (assignment.circulating.experienceYears || 0) <= 2) {
    focuses.push("外回り指導");
  }
  return focuses;
};

// 🔄 既存関数のエクスポート（後方互換性）
export const runAutoStaffAssignment = async (date, debug = false) => {
  console.warn("⚠️ runAutoStaffAssignment is deprecated. Use runEnhancedStaffAssignment instead.");
  return await runEnhancedStaffAssignment(date, debug);
};